import React, { useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box
} from '@mui/material';
import { usePlan } from '../../hooks/PlanProvider';
import { useAuth } from '../../hooks/AuthProvider';
import { motion } from 'motion/react';
import { DraftPlan, SavedPlan } from '../../types/interfaces';
import { Activity } from '../../types/interfaces';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'src/hooks/SnackbarProvider';
import { DashboardHeader } from '../DashboardHeader/DashboardHeader';
import { PlanCard } from '../PlanCard/PlanCard';
import './Dashboard.scss';
import { Button } from '../Button/Button';
import { PlanAnalytics, NavigationAnalytics } from '../../lib/analytics';
import { usePerformanceTracking } from '../../hooks/usePerformanceTracking';

const Dashboard: React.FC = () => {
  // Track performance
  usePerformanceTracking('Dashboard');
  
  const navigate = useNavigate();
  const { userPlans, deletePlan, setSelectedPlan, fetchUserPlans, selectedPlan } = usePlan();
  const { getAuthTokenClaims } = useAuth();
  const userClaims = getAuthTokenClaims();
  const { triggerSnackbar } = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resyDialogOpen, setResyDialogOpen] = useState(false);
  const [resyBookingDetails, setResyBookingDetails] = useState({
    partySize: 2,
    date: new Date(),
    time: '19:00'
  });

  const handleEdit = (plan: SavedPlan) => {
    if (plan) {
      PlanAnalytics.edit(plan.id);
      setSelectedPlan(plan);
      navigate('/map');
    }
  };

  const handleDeleteClick = (plan: SavedPlan) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPlan) return;

    try {
      await deletePlan(selectedPlan as SavedPlan);
      const planId = 'id' in selectedPlan ? selectedPlan.id : 'unknown';
      PlanAnalytics.delete(planId || 'unknown', selectedPlan.activities?.length || 0);
      triggerSnackbar(`Plan '${selectedPlan.name}' deleted successfully!`);
    } catch (err) {
      setError('Failed to delete plan. Please try again.');
      triggerSnackbar(`Failed to delete plan '${selectedPlan.name}'!`);
    } finally {
      setDeleteDialogOpen(false);
      fetchUserPlans();
    }
  };

  const handleResyBook = useCallback((plan: SavedPlan) => {
    setSelectedPlan(plan);
    navigate("/builder")
  }, [setSelectedPlan, navigate]);

  const handleResyBookingSubmit = () => {
    console.log("TODO: Fix resy booking");
  }
  // const handleResyBookingSubmit = useCallback(async () => {
  //   console.log("TODO: Fix resy booking");
  //   if (!selectedPlan?.activities?.[0]) return;

  //   try {
  //     const restaurant = selectedPlan.activities[0];
  //     const venues = await findResyVenue(restaurant.name);

  //     if (venues && venues.length > 0) {
  //       const venue = venues[0];
  //       await bookResyReservation(
  //         venue.id,
  //         resyBookingDetails.time,
  //         resyBookingDetails.partySize
  //       );
  //     }
  //     setResyDialogOpen(false);
  //   } catch (error) {
  //     console.error('Failed to book reservation:', error);
  //     setError('Failed to book reservation. Please try again.');
  //   }
  // }, [selectedPlan, findResyVenue, bookResyReservation, resyBookingDetails]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box className="dashboard-container">
      <DashboardHeader upcomingPlansCount={userPlans.length} />
      <Box className="dashboard-content-area">
        {userPlans.length > 0 ? (
          <>
            <Grid container spacing={3} padding={3}>
              {userPlans.map((plan) => {
                const firstActivity = plan.activities?.[0];
                const photoReference = firstActivity?.photos?.[0]?.photoReference;
                const photoUrl = firstActivity?.photos?.[0]?.url;
                const description = firstActivity?.name || 'Plan details';

                // --- Construct photo URL ---
                let constructedImageUrl: string | undefined = undefined;
                const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

                if (photoUrl) {
                  constructedImageUrl = photoUrl;
                } else if (photoReference && GOOGLE_MAPS_API_KEY) {
                  constructedImageUrl = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${photoReference}&key=${GOOGLE_MAPS_API_KEY}`;
                }
                // --- End URL Construction ---

                return (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <PlanCard
                        id={plan.id}
                        title={plan.name}
                        description={description}
                        imageUrl={constructedImageUrl}
                        onEdit={() => handleEdit(plan)}
                        onDelete={() => handleDeleteClick(plan)}
                        onBook={() => handleResyBook(plan)}
                      />
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>

            <Dialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
            >
              <DialogTitle>Delete Plan</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete "{selectedPlan?.name}"? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)} intent="neutral">Cancel</Button>
                <Button onClick={handleDeleteConfirm} intent="danger">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={resyDialogOpen} onClose={() => setResyDialogOpen(false)}>
              <DialogTitle>Book Restaurant Reservation</DialogTitle>
              <DialogContent>
                <TextField
                  label="Party Size"
                  type="number"
                  value={resyBookingDetails.partySize}
                  onChange={(e) => setResyBookingDetails(prev => ({ ...prev, partySize: parseInt(e.target.value) }))}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Date"
                  type="date"
                  value={resyBookingDetails.date.toISOString().split('T')[0]}
                  onChange={(e) => setResyBookingDetails(prev => ({ ...prev, date: new Date(e.target.value) }))}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Time"
                  type="time"
                  value={resyBookingDetails.time}
                  onChange={(e) => setResyBookingDetails(prev => ({ ...prev, time: e.target.value }))}
                  fullWidth
                  margin="normal"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setResyDialogOpen(false)} intent="neutral">Cancel</Button>
                <Button onClick={handleResyBookingSubmit} intent="primary">
                  Book
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <Box className="no-plans-message" sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">You haven't made any plans!</Typography>
            <Typography variant="body1">Let us help you find something to do.</Typography><br />
            <Button 
              intent="primary" 
              data-testid="explore-button" 
              onClick={() => {
                NavigationAnalytics.bottomNav('/find');
                navigate('/find');
              }}
            >
              AI Planner
            </Button>&emsp;
            <Button 
              intent="primary" 
              data-testid="explore-button" 
              onClick={() => {
                NavigationAnalytics.bottomNav('/map');
                navigate('/map');
              }}
            >
              Map
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
};

export default Dashboard;
